import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Activity,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Truck,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  adminApi,
  type Parcel2GoHealthReport,
  type Parcel2GoCheckResult,
} from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/diagnostics")({
  component: DiagnosticsPage,
});

function DiagnosticsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Diagnostics</h1>
        <p className="text-sm text-muted-foreground">
          Live probes against third-party integrations. Audit-logged.
        </p>
      </div>

      <Parcel2GoHealthCard />
    </div>
  );
}

function Parcel2GoHealthCard() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<Parcel2GoHealthReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  async function run() {
    setRunning(true);
    setError(null);
    try {
      const fn = adminApi.parcel2GoHealth();
      const result = await fn({});
      setReport(result.data);
    } catch (e) {
      setError((e as Error).message ?? "Probe failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Parcel2Go integration
          </CardTitle>
          {report ? (
            <OverallPill ok={report.ok} ranAt={report.ranAt} />
          ) : (
            <span className="text-xs text-muted-foreground">not run</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run()} disabled={running}>
            {running ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running…
              </>
            ) : (
              <>
                <Activity className="mr-2 h-4 w-4" />
                Run health probe
              </>
            )}
          </Button>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {report && (
          <div className="space-y-3 pt-2">
            <CheckRow
              label="Auth — OAuth2 client credentials"
              hint="getBalance() round-trip proves the client id/secret pair is accepted."
              result={report.auth}
              renderDetail={(d) => {
                const x = d as { currency?: string };
                return <span>Token accepted{x.currency ? ` · account currency ${x.currency}` : ""}</span>;
              }}
            />
            <CheckRow
              label="Drop shops — service-point search"
              hint="findDropPoints() for the default network at a fixed test postcode."
              result={report.dropshops}
              renderDetail={(d) => {
                const x = d as { count?: number; first?: string | null };
                return (
                  <>
                    <span>Found {x.count ?? 0} point(s)</span>
                    {x.first && <span className="text-muted-foreground"> · {x.first}</span>}
                  </>
                );
              }}
            />
            <CheckRow
              label="PrePay — float balance"
              hint="Alerts when the balance drops below the configured threshold."
              result={report.prepay}
              renderDetail={(d) => {
                const x = d as {
                  amount?: number;
                  currency?: string;
                  threshold?: number;
                  belowThreshold?: boolean;
                };
                return (
                  <span className={cn(x.belowThreshold && "text-destructive")}>
                    £{x.amount?.toFixed(2) ?? "—"} {x.currency ?? ""} · threshold £
                    {x.threshold?.toFixed(2) ?? "—"}
                    {x.belowThreshold ? " · BELOW THRESHOLD" : ""}
                  </span>
                );
              }}
            />
            <CheckRow
              label="Ops alerts — recent carrier alerts"
              hint="Latest opsAlerts docs (portal-cancel needed, PrePay low, auth-release failures)."
              result={report.opsAlerts}
              renderDetail={(d) => {
                const x = d as {
                  recentCount?: number;
                  recent?: { id: string; type?: string; createdAt?: string }[];
                };
                if (!x.recentCount) return <span>No recent alerts</span>;
                return (
                  <div className="space-y-0.5">
                    {x.recent?.map((a) => (
                      <div key={a.id} className="font-mono text-[11px]">
                        {a.type ?? "unknown"}{" "}
                        <span className="text-muted-foreground">
                          {a.createdAt ? new Date(a.createdAt).toLocaleString("en-GB") : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              }}
            />

            <button
              type="button"
              onClick={() => setShowRaw((v) => !v)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {showRaw ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              Raw response
            </button>
            {showRaw && (
              <pre className="overflow-x-auto rounded-md border bg-muted p-3 text-xs">
                {JSON.stringify(report, null, 2)}
              </pre>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OverallPill({ ok, ranAt }: { ok: boolean; ranAt: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
        ok
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
          : "bg-destructive/10 text-destructive",
      )}
      title={new Date(ranAt).toLocaleString("en-GB")}
    >
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {ok ? "all green" : "issues found"}
    </span>
  );
}

function CheckRow({
  label,
  hint,
  result,
  renderDetail,
}: {
  label: string;
  hint: string;
  result: Parcel2GoCheckResult;
  renderDetail: (detail: unknown) => React.ReactNode;
}) {
  return (
    <div className="rounded-md border bg-card/50 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          {result.ok ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          ) : (
            <XCircle className="h-4 w-4 text-destructive" />
          )}
          {label}
        </div>
        {result.ms !== undefined && (
          <span className="text-xs text-muted-foreground">{result.ms}ms</span>
        )}
      </div>
      <div className="ml-6 mt-1 space-y-1 text-xs">
        <div className="text-muted-foreground">{hint}</div>
        {result.detail !== undefined && (
          <div className="text-foreground">{renderDetail(result.detail)}</div>
        )}
        {result.error && (
          <div className="text-destructive">error: {result.error}</div>
        )}
      </div>
    </div>
  );
}
